import { getLegalCertificateProgress } from "api/legalCertificate";

const WORKFLOW_STEP_TO_PATH: Record<
  string,
  { section: "discussion" | "filing" | "status"; slug: string }
> = {
  LC_S1_01_PRELIMINARY_DISCUSSION: {
    section: "discussion",
    slug: "preliminary-discussion-with-the-client",
  },
  LC_S1_02_APPLICATION_DETAILS_FROM_CLIENT: {
    section: "discussion",
    slug: "application-details-from-client",
  },
  LC_S1_03_INVOICE_TO_CLIENT: {
    section: "discussion",
    slug: "invoice-to-client",
  },
  LC_S1_04_PAYMENT_FROM_CLIENT: {
    section: "discussion",
    slug: "payment-from-client-for-legal-certificate",
  },
  LC_S2_01_STAMP_PAPER_DETAILS_TO_VENDOR: {
    section: "filing",
    slug: "stamp-paper-details-to-vendor",
  },
  LC_S2_02_STAMP_PAPER_FROM_VENDOR: {
    section: "filing",
    slug: "stamp-paper-from-vendor",
  },
  LC_S2_03_PREPARATION_OF_POA: {
    section: "filing",
    slug: "preparation-of-power-of-attorney",
  },
  LC_S2_03A_POA_FIELDS_SAVED: {
    section: "filing",
    slug: "preparation-of-power-of-attorney",
  },
  LC_S2_03B_POA_PDF_GENERATED: {
    section: "filing",
    slug: "preparation-of-power-of-attorney",
  },
  LC_S2_04_FILE_FORMS_FOR_LEGAL_CERTIFICATE: {
    section: "filing",
    slug: "file-forms",
  },
  LC_S2_05_INFORM_CLIENT_OF_FILING: {
    section: "filing",
    slug: "inform-client-of-filing",
  },
  LC_S3_01_LEGAL_CERTIFICATE_RECEIVED: {
    section: "status",
    slug: "legal-certificate-received",
  },
  LC_S3_02_PREPARE_KIT: {
    section: "status",
    slug: "prepare-kit",
  },
  LC_S3_02A_COVERING_NOTE_FIELDS_SAVED: {
    section: "status",
    slug: "prepare-kit",
  },
  LC_S3_02B_COVERING_NOTE_PDF_GENERATED: {
    section: "status",
    slug: "prepare-kit",
  },
  LC_S3_03_HAND_OVER_KIT_TO_CLIENT: {
    section: "status",
    slug: "hand-over-kit-to-client",
  },
  LC_S3_04_NOTICE_TO_CLIENT: {
    section: "status",
    slug: "notice-to-client",
  },
  LC_S3_05_INFORM_CLIENT_OF_LEGAL_CERTIFICATE: {
    section: "status",
    slug: "inform-client-about-legal-certificate",
  },
};

const DEFAULT_STEP = {
  section: "discussion" as const,
  slug: "preliminary-discussion-with-the-client",
};

/**
 * Resolves the in-app path for a legal-certificate lead's most recently completed
 * workflow step so the user lands directly on it from pending items.
 *
 * Falls back to the preliminary discussion step when the lead has no completed
 * steps yet, when the step has no implemented UI route, or when progress fails.
 */
export async function getLegalCertificatePathForLead(
  leadId: string
): Promise<string> {
  try {
    const progress = await getLegalCertificateProgress(leadId);
    const lastCompleted = [...progress.steps]
      .reverse()
      .find((s) => s.isCompleted)?.slug;
    const stepKey =
      lastCompleted ?? progress.maxStepReached ?? progress.currentStep ?? null;
    const pathSpec = stepKey ? WORKFLOW_STEP_TO_PATH[stepKey] : null;
    const { section, slug } = pathSpec ?? DEFAULT_STEP;
    return `/dashboard/legal-certificate/new/${section}/${slug}/${leadId}`;
  } catch {
    return `/dashboard/legal-certificate/new/discussion/preliminary-discussion-with-the-client/${leadId}`;
  }
}
